import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { 
  fetchComplianceReportData, 
  exportToCSV, 
  downloadCSV, 
  exportToPDF, 
  downloadPDF,
  getAvailableEventTypes,
  getAvailableEventCategories,
  getAvailableSeverityLevels
} from '../../services/complianceReportService';
import { useMultipleRealtimeSubscriptions } from '../../hooks/useRealtimeSubscription';
import { format } from 'date-fns';

const ComplianceReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);
  
  const [filters, setFilters] = useState({
    eventType: '',
    eventCategory: '',
    severity: '',
    dateFrom: '',
    dateTo: '',
    userEmail: '',
    limit: 500
  });

  useEffect(() => {
    loadReportData();
  }, [filters]);

  // Real-time subscription to audit_logs table
  useMultipleRealtimeSubscriptions([
    {
      table: 'audit_logs',
      onInsert: (newLog) => {
        console.log('New audit log created:', newLog);
        loadReportData();
      },
      onUpdate: (updatedLog) => {
        console.log('Audit log updated:', updatedLog);
        loadReportData();
      }
    }
  ]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      const { data, error } = await fetchComplianceReportData(filters);
      
      if (error) {
        console.error('Error loading compliance report:', error);
      } else {
        setReportData(data);
      }
    } catch (error) {
      console.error('Exception loading compliance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSelectedLogs([]); // Clear selection when filters change
  };

  const handleResetFilters = () => {
    setFilters({
      eventType: '',
      eventCategory: '',
      severity: '',
      dateFrom: '',
      dateTo: '',
      userEmail: '',
      limit: 500
    });
    setSelectedLogs([]);
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => {
      if (prev?.includes(logId)) {
        return prev?.filter(id => id !== logId);
      } else {
        return [...prev, logId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLogs?.length === reportData?.logs?.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(reportData?.logs?.map(log => log?.id) || []);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const logsToExport = selectedLogs?.length > 0 
        ? reportData?.logs?.filter(log => selectedLogs?.includes(log?.id))
        : reportData?.logs;
      
      const csvContent = exportToCSV(logsToExport, filters);
      const filename = `compliance-report-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
      downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Fehler beim Exportieren der CSV-Datei');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const logsToExport = selectedLogs?.length > 0 
        ? reportData?.logs?.filter(log => selectedLogs?.includes(log?.id))
        : reportData?.logs;
      
      const pdfDoc = exportToPDF(logsToExport, reportData?.statistics, filters);
      const filename = `compliance-report-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.pdf`;
      downloadPDF(pdfDoc, filename);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Fehler beim Exportieren der PDF-Datei');
    } finally {
      setExporting(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'security':
        return 'Shield';
      case 'compliance':
        return 'FileCheck';
      case 'data_modification':
        return 'Edit';
      case 'system':
        return 'Settings';
      default:
        return 'Activity';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const eventTypes = getAvailableEventTypes();
  const eventCategories = getAvailableEventCategories();
  const severityLevels = getAvailableSeverityLevels();

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
                <Icon name="FileText" size={32} />
                Compliance-Berichte
              </h1>
              <p className="text-muted-foreground mt-2">
                Generierung von Compliance-Berichten mit CSV/PDF-Export und konfigurierbaren Datumsbereichen
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              onClick={loadReportData}
              disabled={loading}
            >
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Statistics Summary */}
        {reportData?.statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Gesamt-Ereignisse</p>
                  <p className="text-3xl font-bold text-foreground">{reportData?.statistics?.totalEvents || 0}</p>
                </div>
                <Icon name="Activity" size={32} className="text-primary" />
              </div>
            </div>
            
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Kritische Ereignisse</p>
                  <p className="text-3xl font-bold text-red-600">{reportData?.statistics?.bySeverity?.critical || 0}</p>
                </div>
                <Icon name="AlertTriangle" size={32} className="text-red-600" />
              </div>
            </div>
            
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Warnungen</p>
                  <p className="text-3xl font-bold text-yellow-600">{reportData?.statistics?.bySeverity?.warning || 0}</p>
                </div>
                <Icon name="AlertCircle" size={32} className="text-yellow-600" />
              </div>
            </div>
            
            <div className="bg-card p-6 rounded-lg border border-border shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Benutzer</p>
                  <p className="text-3xl font-bold text-foreground">{reportData?.statistics?.uniqueUsers || 0}</p>
                </div>
                <Icon name="Users" size={32} className="text-primary" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Filter" size={20} />
              Filter
            </h2>
            <Button
              variant="ghost"
              size="sm"
              iconName="RotateCcw"
              onClick={handleResetFilters}
            >
              Zurücksetzen
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Ereignistyp
              </label>
              <select
                value={filters?.eventType}
                onChange={(e) => handleFilterChange('eventType', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Alle Ereignistypen</option>
                {eventTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>{type?.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Kategorie
              </label>
              <select
                value={filters?.eventCategory}
                onChange={(e) => handleFilterChange('eventCategory', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Alle Kategorien</option>
                {eventCategories?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Schweregrad
              </label>
              <select
                value={filters?.severity}
                onChange={(e) => handleFilterChange('severity', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Alle Schweregrade</option>
                {severityLevels?.map(level => (
                  <option key={level?.value} value={level?.value}>{level?.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Benutzer-E-Mail
              </label>
              <input
                type="text"
                value={filters?.userEmail}
                onChange={(e) => handleFilterChange('userEmail', e?.target?.value)}
                placeholder="E-Mail filtern"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Von Datum
              </label>
              <input
                type="date"
                value={filters?.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Bis Datum
              </label>
              <input
                type="date"
                value={filters?.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Limit
              </label>
              <select
                value={filters?.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e?.target?.value))}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={100}>100 Einträge</option>
                <option value={250}>250 Einträge</option>
                <option value={500}>500 Einträge</option>
                <option value={1000}>1000 Einträge</option>
              </select>
            </div>
          </div>
        </div>

        {/* Export Controls */}
        <div className="bg-card p-6 rounded-lg border border-border shadow-sm mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Icon name="Download" size={20} />
                Export
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedLogs?.length > 0 
                  ? `${selectedLogs?.length} Ereignis(se) ausgewählt`
                  : `Alle ${reportData?.logs?.length || 0} Ereignisse werden exportiert`
                }
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                iconName="FileText"
                onClick={handleExportCSV}
                disabled={exporting || !reportData?.logs?.length}
              >
                CSV exportieren
              </Button>
              <Button
                variant="default"
                iconName="FileText"
                onClick={handleExportPDF}
                disabled={exporting || !reportData?.logs?.length}
              >
                PDF exportieren
              </Button>
            </div>
          </div>
        </div>

        {/* Audit Logs Table */}
        <div className="bg-card rounded-lg border border-border shadow-sm">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Audit-Ereignisse ({reportData?.logs?.length || 0})
              </h2>
              {reportData?.logs?.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Check"
                  onClick={handleSelectAll}
                >
                  {selectedLogs?.length === reportData?.logs?.length ? 'Alle abwählen' : 'Alle auswählen'}
                </Button>
              )}
            </div>
          </div>
          
          <div className="overflow-auto max-h-[600px]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : reportData?.logs?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Keine Audit-Ereignisse gefunden</p>
                <p className="text-sm text-muted-foreground mt-2">Passen Sie die Filter an, um Ergebnisse zu sehen</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {reportData?.logs?.map((log) => (
                  <div
                    key={log?.id}
                    className="p-4 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        checked={selectedLogs?.includes(log?.id)}
                        onChange={() => handleSelectLog(log?.id)}
                        className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                          <Icon name={getCategoryIcon(log?.eventCategory)} size={18} className="text-primary flex-shrink-0" />
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(log?.severity)}`}>
                            {log?.severity?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(log?.createdAt)}
                          </span>
                          {log?.userEmail && (
                            <span className="text-xs text-muted-foreground">
                              von {log?.userEmail}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm font-medium text-foreground mb-1">
                          {log?.actionDescription}
                        </p>
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {log?.entityName && (
                            <span>Entität: {log?.entityType} - {log?.entityName}</span>
                          )}
                          {log?.dealershipName && (
                            <span>Partner: {log?.dealershipName}</span>
                          )}
                        </div>
                        
                        {(log?.oldValues || log?.newValues || log?.metadata) && (
                          <button
                            onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
                            className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                          >
                            <Icon name={expandedLog === log?.id ? 'ChevronUp' : 'ChevronDown'} size={14} />
                            {expandedLog === log?.id ? 'Details ausblenden' : 'Details anzeigen'}
                          </button>
                        )}
                        
                        {expandedLog === log?.id && (
                          <div className="mt-3 p-3 bg-muted/50 rounded border border-border">
                            {log?.oldValues && (
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-foreground mb-1">Alte Werte:</p>
                                <pre className="text-xs text-muted-foreground overflow-auto">
                                  {JSON.stringify(log?.oldValues, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log?.newValues && (
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-foreground mb-1">Neue Werte:</p>
                                <pre className="text-xs text-muted-foreground overflow-auto">
                                  {JSON.stringify(log?.newValues, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log?.metadata && (
                              <div>
                                <p className="text-xs font-semibold text-foreground mb-1">Metadaten:</p>
                                <pre className="text-xs text-muted-foreground overflow-auto">
                                  {JSON.stringify(log?.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceReports;